<script setup>
import IconMail from "../icons/IconMail.vue";
import { site, navLinks, legalLinks, storeLinks } from "../../content/site";
import { scrollToSection } from "../../composables/useScrollTo";
import { useLanding } from "../../composables/useLanding";
import logo from "@/assets/applates2_logo.png";

const landing = useLanding();
const { tagline, crossLink } = landing.footer;

const year = new Date().getFullYear();
</script>

<template>
  <footer class="footer">
    <div class="container footer__inner">
      <div class="footer__brand">
        <router-link :to="landing.path" class="footer__logo">
          <img :src="logo" alt="" width="40" height="40" />
          <span>{{ site.nameEn }}</span>
        </router-link>
        <p class="footer__tagline">
          {{ tagline }}
        </p>
        <a :href="`mailto:${site.email}`" class="footer__email">
          <IconMail aria-hidden="true" />
          {{ site.email }}
        </a>
      </div>

      <nav class="footer__col" aria-label="ניווט בתחתית העמוד">
        <h2 class="footer__heading">באתר</h2>
        <ul role="list">
          <li v-for="link in navLinks" :key="link.target">
            <button class="footer__link" @click="scrollToSection(link.target)">
              {{ link.label }}
            </button>
          </li>
          <li v-if="crossLink">
            <router-link class="footer__link" :to="crossLink.to">
              {{ crossLink.label }}
            </router-link>
          </li>
        </ul>
      </nav>

      <nav class="footer__col" aria-label="הורדת האפליקציה">
        <h2 class="footer__heading">האפליקציה</h2>
        <ul role="list">
          <li>
            <router-link class="footer__link" to="/download">
              הורדת האפליקציה
            </router-link>
          </li>
          <li>
            <a
              class="footer__link"
              :href="storeLinks.ios"
              target="_blank"
              rel="noopener noreferrer"
              >App Store</a
            >
          </li>
          <li>
            <a
              class="footer__link"
              :href="storeLinks.android"
              target="_blank"
              rel="noopener noreferrer"
              >Google Play</a
            >
          </li>
        </ul>
      </nav>

      <nav class="footer__col" aria-label="מידע משפטי">
        <h2 class="footer__heading">מידע</h2>
        <ul role="list">
          <li v-for="link in legalLinks" :key="link.to">
            <router-link class="footer__link" :to="link.to">
              {{ link.label }}
            </router-link>
          </li>
        </ul>
      </nav>
    </div>

    <div class="container footer__bottom">
      <p>{{ site.name }}, כל הזכויות שמורות © {{ year }}</p>
    </div>
  </footer>
</template>

<style scoped>
.footer {
  background: var(--ink);
  color: var(--on-ink-muted);
  padding-block: var(--space-9) var(--space-5);
}

.footer__inner {
  display: grid;
  gap: var(--space-7);
}

.footer__logo {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--on-ink);
  font-size: var(--text-lg);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-wide);
}

.footer__logo img {
  inline-size: 40px;
  block-size: 40px;
  border-radius: var(--radius-sm);
}

.footer__tagline {
  margin-block: var(--space-3);
  max-inline-size: 32ch;
  font-size: var(--text-sm);
}

.footer__email {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  transition: color var(--dur-fast) var(--ease-in-out);
}

.footer__email:hover {
  color: var(--on-ink);
}

.footer__email :deep(svg) {
  inline-size: 18px;
  block-size: 18px;
}

.footer__heading {
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: var(--weight-bold);
  letter-spacing: var(--tracking-wide);
  color: var(--on-ink);
  margin-block-end: var(--space-4);
}

.footer__col ul {
  display: grid;
  gap: var(--space-3);
  padding: 0;
  margin: 0;
  list-style: none;
}

.footer__link {
  font-size: var(--text-sm);
  color: var(--on-ink-muted);
  text-align: start;
  transition: color var(--dur-fast) var(--ease-in-out),
    padding-inline-start var(--dur-fast) var(--ease-out);
}

.footer__link:hover {
  color: var(--on-ink);
  padding-inline-start: var(--space-2);
}

.footer__bottom {
  margin-block-start: var(--space-8);
  padding-block-start: var(--space-5);
  border-block-start: 1px solid rgba(255, 255, 255, 0.12);
  font-size: var(--text-sm);
  text-align: center;
}

.footer__bottom p {
  margin: 0;
}

@media (min-width: 760px) {
  .footer__inner {
    grid-template-columns: 1.6fr repeat(3, 1fr);
    gap: var(--space-6);
  }
}
</style>
